// This file is a direct merge of the cleaned FeatureRequestModal implementation
// for safe replacement. It mirrors `FeatureRequestModalClean.js` and can be
// copied into `FeatureRequestModal.js` or used as a reference.
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './FeatureRequestModal.css';

const FeatureRequestModal = ({ onClose, backendUrl, onSent }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('Feature request from web UI');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('user');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.name) setName(parsed.name);
        if (parsed.email) setEmail(parsed.email || parsed.preferred_username || parsed.email);
      }
    } catch (e) { /* ignore */ }
  }, []);

  const baseUrl = (backendUrl || '').toString().replace(/^['"]+|['"]+$/g, '').trim();

  const descRef = useRef(null);
  const modalRef = useRef(null);
  useEffect(() => { try { descRef.current && descRef.current.focus(); } catch(e){} }, []);

  // Focus trap + Escape-to-close
  useEffect(() => {
    const prev = document.activeElement;
    const root = modalRef.current;
    if (!root) return undefined;

    const focusableSelector = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const getFocusables = () => Array.from(root.querySelectorAll(focusableSelector)).filter(el => el.offsetParent !== null);

    const focusables = getFocusables();
    try { if (descRef.current) descRef.current.focus(); else if (focusables.length) focusables[0].focus(); } catch(e){}

    const handleKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); try { onClose && onClose(); } catch(_){}; return; }
      if (e.key === 'Tab') {
        const list = getFocusables(); if (!list.length) return; const first = list[0]; const last = list[list.length-1];
        if (e.shiftKey) { if (document.activeElement === first) { e.preventDefault(); last.focus(); } }
        else { if (document.activeElement === last) { e.preventDefault(); first.focus(); } }
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('keydown', handleKey); try { prev && prev.focus(); } catch(_){} };
  }, [onClose]);

  // Toggle error class on modal for error-state focus-ring variant
  useEffect(() => {
    const el = modalRef.current;
    if (!el) return;
    if (status && status.ok === false) el.classList.add('has-error'); else el.classList.remove('has-error');
  }, [status]);

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) { setFile(null); setFileError(null); return; }
    const allowed = ['image/jpeg','image/png','image/webp','image/gif','application/pdf'];
    const max = 5 * 1024 * 1024;
    if (!allowed.includes(f.type)) { setFile(null); setFileError('Allowed: JPG, PNG, WebP, GIF, PDF'); return; }
    if (f.size > max) { setFile(null); setFileError('Max file size 5 MB'); return; }
    setFile(f); setFileError(null);
  };

  const isImage = (f) => f && f.type && f.type.startsWith('image/');

  const submit = async (e) => {
    e && e.preventDefault();
    if (!description || description.trim().length === 0) { setStatus({ ok:false, msg: 'Please add a description.' }); return; }
    setIsSubmitting(true); setStatus(null);
    try {
      let res;
      if (file) {
        const form = new FormData(); form.append('name', name); form.append('email', email); form.append('subject', subject); form.append('description', description); form.append('attachment', file);
        res = await fetch(`${baseUrl}/api/feature-request`, { method: 'POST', body: form });
      } else {
        res = await fetch(`${baseUrl}/api/feature-request`, { method: 'POST', headers: { 'Content-Type':'application/json' }, body: JSON.stringify({ name, email, subject, description }) });
      }
      if (res.ok) {
        setStatus({ ok:true, msg: 'Thanks — your request is received.' });
        setName(''); setEmail(''); setSubject('Feature request from web UI'); setDescription(''); setFile(null);
        if (typeof onSent === 'function') { try { onSent(); } catch(e){} }
        setTimeout(() => { try { onClose && onClose(); } catch(e){} }, 900);
      } else {
        const body = await res.json().catch(()=>({}));
        setStatus({ ok:false, msg: body.error || 'Failed to send' });
      }
    } catch (err) { console.error(err); setStatus({ ok:false, msg: 'Network error' }); }
    finally { setIsSubmitting(false); }
  };

  const content = (
    <div className="fr-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="fr-title">
      <div className="fr-modal-card" ref={modalRef}>
        <header className="fr-modal-header">
          <h3 id="fr-title">Suggest a Feature</h3>
          <button className="fr-close" onClick={onClose} aria-label="Close">×</button>
        </header>
        <form className="fr-form" onSubmit={submit}>
          <label> Your name <input value={name} onChange={e=>setName(e.target.value)} placeholder="Optional"/></label>
          <label> Email <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="Optional"/></label>
          <label> Subject <input value={subject} onChange={e=>setSubject(e.target.value)} /></label>
          <label> Description <textarea ref={descRef} value={description} onChange={e=>setDescription(e.target.value)} rows={6} placeholder="Describe the feature..." /></label>
          <label> Attachment (optional) <input type="file" accept="image/*,application/pdf" onChange={handleFileChange} /></label>
          {fileError && <div className="fr-status err">{fileError}</div>}
          {file && isImage(file) && <div className="fr-file-preview"><img src={URL.createObjectURL(file)} alt="preview"/></div>}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:12}}>
            <div>
              <button className="btn" type="submit" disabled={isSubmitting}>{isSubmitting? 'Sending...' : 'Send Request'}</button>
              <button className="btn ghost" type="button" onClick={onClose} style={{marginLeft:8}}>Cancel</button>
            </div>
            <div>
              {status && <div role="status" {...(status.ok ? { 'aria-live': 'polite', 'aria-atomic': 'true' } : {})} className={`fr-status ${status.ok ? 'ok' : 'err'}`}>{status.msg}</div>}
            </div>
          </div>
          <div className="sr-only" aria-live="polite" aria-atomic="true">{status && status.ok ? status.msg : ''}</div>
        </form>
      </div>
    </div>
  );

  try { return createPortal(content, document.body); } catch(e) { return content; }
};

export default FeatureRequestModal;
